import { useMemo } from "react";

import type { Mechanic } from "@/@types/gamification";
import { useGetCompetingCampaignsQuery } from "@/services/gamification";
import { isEndDateAfterStartDate } from "@/utils/date-string";

import {
  classifyCompetingCampaigns,
  type CompetingCampaignsResult,
} from "../utils/competing-campaigns";

export const CompetingPanelState = {
  MISSING_PERIOD: "MISSING_PERIOD",
  LOADING: "LOADING",
  ERROR: "ERROR",
  EMPTY: "EMPTY",
  READY: "READY",
} as const;

export type CompetingPanelState =
  (typeof CompetingPanelState)[keyof typeof CompetingPanelState];

export interface UseCompetingCampaignsParams {
  campaignType: Mechanic;
  startDate?: string;
  endDate?: string;
  priority: unknown;
  competes: boolean;
  group?: string | null;
  currentCampaignId?: number;
  currentCreatedAt?: string;
}

export interface UseCompetingCampaignsResult {
  state: CompetingPanelState;
  result: CompetingCampaignsResult | null;
  truncated: boolean;
  retry: () => void;
}

const hasValidPeriod = (startDate?: string, endDate?: string): boolean =>
  Boolean(startDate && endDate) &&
  isEndDateAfterStartDate(startDate as string, endDate as string);

interface StateInputs {
  periodReady: boolean;
  isLoading: boolean;
  isError: boolean;
  result: CompetingCampaignsResult | null;
}

function resolveState({
  periodReady,
  isLoading,
  isError,
  result,
}: StateInputs): CompetingPanelState {
  if (!periodReady) return CompetingPanelState.MISSING_PERIOD;
  if (isLoading) return CompetingPanelState.LOADING;
  if (isError || !result) return CompetingPanelState.ERROR;
  const hasRivals = result.sameCustomer.length + result.sameGame.length > 0;
  return hasRivals ? CompetingPanelState.READY : CompetingPanelState.EMPTY;
}

export function useCompetingCampaigns(
  params: UseCompetingCampaignsParams,
): UseCompetingCampaignsResult {
  const periodReady = hasValidPeriod(params.startDate, params.endDate);

  const { currentData, isFetching, isError, refetch } =
    useGetCompetingCampaignsQuery(
      { startDate: params.startDate ?? "", endDate: params.endDate ?? "" },
      { skip: !periodReady },
    );

  const {
    campaignType,
    priority,
    competes,
    group,
    currentCampaignId,
    currentCreatedAt,
  } = params;

  const result = useMemo<CompetingCampaignsResult | null>(() => {
    if (!currentData) return null;
    return classifyCompetingCampaigns({
      campaigns: currentData.campaigns,
      campaignType,
      priority,
      competes,
      group,
      currentCampaignId,
      currentCreatedAt,
    });
  }, [currentData, campaignType, priority, competes, group, currentCampaignId, currentCreatedAt]);

  const state = resolveState({
    periodReady,
    isLoading: isFetching && !currentData,
    isError,
    result,
  });

  return {
    state,
    result,
    truncated: currentData?.truncated ?? false,
    retry: () => {
      void refetch();
    },
  };
}
